import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {validateEndDate} from 'src/modules/profile/account/tabs/license-and-features/report-tab/components/utils/dateValidationUtils';
import {
    fetchNotification,
    updateNotification,
} from 'src/openapi/admin/apis/InternalNotificationApi';
import {useAppDispatch} from 'src/store/hooks';

import {validateContentSize} from '../content-builder/utils';

import type {Notification} from 'src/openapi/admin/models/Notification';

type Props = {
    notificationId: string,
};

export const useEditNotification = (props: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback((signal: AbortSignal) => apiDispatch(
        fetchNotification({notificationId: props.notificationId}, {signal}),
    ), [props.notificationId, apiDispatch]);

    const saveDispatchAction = useCallback((
        {notificationId, ...updateNotificationRequest}: Notification,
        signal: AbortSignal,
    ) => apiDispatch(
        updateNotification({
            updateNotificationRequest: {...updateNotificationRequest, content: updateNotificationRequest.content || ''},
            notificationId,
        }, {signal}),
    ), [apiDispatch]);

    const editValidation = useCallback((data: Notification) => {
        const sizeExceedError = validateContentSize(data.content || '');
        if (sizeExceedError) {
            addError(sizeExceedError);
        } else {
            removeError('notification-content');
        }

        const endDateError = validateEndDate(data.endDate, data.startDate, 'notification-end-date');
        if (endDateError) {
            addError(endDateError);
        } else {
            removeError('notification-end-date');
        }
    }, [addError, removeError]);

    const saveValidation = useCallback((data: Notification) => {
        let valid = true;
        const sizeExceedError = validateContentSize(data.content || '');
        if (sizeExceedError) {
            valid = false;
            addError(sizeExceedError);
        } else {
            removeError('notification-content');
        }

        const endDateError = validateEndDate(data.endDate, data.startDate, 'notification-end-date');
        if (endDateError) {
            valid = false;
            addError(endDateError);
        } else {
            removeError('notification-end-date');
        }

        return valid;
    }, [addError, removeError]);

    return useCommonDataHook({
        initValue: {
            notificationId: '',
            content: '',
            title: '',
            topic: '',
            startDate: '',
            endDate: '',
            adminProfileIds: [],
        },
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
